import { useState } from "react";
import type { FC } from "react";
import WebApp from "@twa-dev/sdk";
import { generateBotConfig, createBot } from "../api/client";

interface Props {
  user: ReturnType<typeof import("../hooks/useTelegram").useTelegram>["user"];
  onBack: () => void;
  onCreated: (botId: string) => void;
}

type Step = "describe" | "review" | "token" | "saving";

export const CreateBotPage: FC<Props> = ({ user, onBack, onCreated }) => {
  const [step, setStep] = useState<Step>("describe");
  const [description, setDescription] = useState("");
  const [generated, setGenerated] = useState<{
    config: Record<string, unknown>;
    summary: string;
    explanation?: string;
    steps?: Array<{ title: string; description: string; value: string }>;
  } | null>(null);
  const [botToken, setBotToken] = useState("");
  const [botUsername, setBotUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerate = async () => {
    if (description.trim().length < 5) {
      setError("Iltimos, biznes haqida batafsilroq yozing (kamida 5 belgi).");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await generateBotConfig(description.trim());
      setGenerated({
        config: result.config,
        summary: result.summary,
        explanation: result.explanation,
        steps: result.steps,
      });
      setStep("review");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Xatolik yuz berdi");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setStep("token");
  };

  const handleSave = async () => {
    if (!botToken.trim() || !botUsername.trim()) {
      setError("Bot token va username kerak.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await createBot({
        config: generated!.config,
        ownerTelegramId: user?.id ?? 0,
        botToken: botToken.trim(),
        botUsername: botUsername.trim().replace("@", ""),
      });
      onCreated(result.botId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Saqlashda xatolik");
      setLoading(false);
    }
  };

  const steps = ["describe", "review", "token"];
  const stepIndex = steps.indexOf(step);

  return (
    <div className="create-bot-page">
      <header className="page-header">
        <button onClick={onBack} className="back-btn">← Orqaga</button>
        <h1>Yangi bot yaratish</h1>
      </header>

      <div className="step-indicator">
        {["Tavsif", "Ko'rib chiqish", "Token"].map((label, i) => (
          <div key={label} className={`step-dot ${i <= stepIndex ? "active" : ""}`} />
        ))}
      </div>

      {error && (
        <div className="error-card">
          <p className="error-text">{error}</p>
        </div>
      )}

      {/* Step 1: Describe your business */}
      {step === "describe" && (
        <div className="wizard-step">
          <h2>Biznesingiz haqida yozing</h2>
          <p className="hint-text">
            Bot nima qilishi kerak? Qanday biznes? Xizmat turlari, ish vaqti, narxlar haqida yozing.
            AI avtomatik tarzda bot konfiguratsiyasini yaratadi.
          </p>
          <textarea
            className="input input-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Masalan: Mening sartaroshxonam bor. Duushanbadan shanbagacha 9:00-18:00 gacha ishlaymiz. Xizmatlar: soch qirqish (50,000 so'm), soch bo'yash (150,000 so'm). Manzil: Toshkent, Chilonzor..."
            rows={8}
            disabled={loading}
          />
          <button
            onClick={handleGenerate}
            disabled={loading || description.trim().length < 5}
            className="btn btn-primary btn-block"
          >
            {loading ? "AI o'ylamoqda..." : "Bot yaratish →"}
          </button>
        </div>
      )}

      {/* Step 2: Review AI-generated config */}
      {step === "review" && generated && (
        <div className="wizard-step">
          <h2>Botingiz qanday sozlandi</h2>
          <div className="ai-summary-card">
            <h3>🏢 {generated.config.businessName as string}</h3>
            {generated.explanation && (
              <p className="hint-text">{generated.explanation}</p>
            )}
          </div>

          {generated.steps && generated.steps.length > 0 && (
            <div className="steps-list">
              {generated.steps.map((step, i) => (
                <div key={i} className="step-item">
                  <div className="step-header">
                    <h4>{step.title}</h4>
                    <p className="step-desc">{step.description}</p>
                  </div>
                  <div className="step-value">
                    <code>{step.value}</code>
                  </div>
                </div>
              ))}
            </div>
          )}

          <details className="config-details">
            <summary>📋 To'liq JSON konfiguratsiya</summary>
            <pre className="config-json">{JSON.stringify(generated.config, null, 2)}</pre>
          </details>

          <div className="wizard-buttons">
            <button onClick={() => setStep("describe")} className="btn btn-secondary">
              Qayta yozish
            </button>
            <button onClick={handleConfirm} className="btn btn-primary">
              Tasdiqlash →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Enter bot token */}
      {step === "token" && (
        <div className="wizard-step">
          <h2>Bot tokenini kiriting</h2>
          <p className="hint-text">
            <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer">@BotFather</a> orqali
            yangi bot yarating va tokenini quyida kiriting.
          </p>

          <label className="input-label">
            Bot token
            <input
              className="input"
              type="text"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456:ABC-DEF1234ghikl..."
              disabled={loading}
            />
          </label>

          <label className="input-label">
            Bot username
            <input
              className="input"
              type="text"
              value={botUsername}
              onChange={(e) => setBotUsername(e.target.value)}
              placeholder="my_business_bot"
              disabled={loading}
            />
          </label>

          <div className="wizard-buttons">
            <button onClick={() => setStep("review")} className="btn btn-secondary" disabled={loading}>
              ← Orqaga
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !botToken.trim() || !botUsername.trim()}
              className="btn btn-primary"
            >
              {loading ? "Saqlanmoqda..." : "Botni faollashtirish"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
